import { useStore } from 'vuex';
import { ref } from 'vue';
import { IGiftItem, } from '@/pages/room/components/gift-control/gift-types';
import giftService from '@/service/gift';
import { imageToBase64, defer, IDeferrer, sleep } from '@/utils';
import Storage from '@/utils/storage';

const STORAGE_KEY = 'gift_config';

export default class GiftCache {

  // 转 base64 存 localstorage 数量
  static base64Count = 6;

  // 是否预加载过大于 base64Count 后面的图片
  static hasPreloadTail = false;

  // 是否最新数据，从服务获取后会置为true
  static isLatest = false;

  // 更新缓存定时器
  static timer: number | null = null;

  // 从服务获取数据未返回时的 promise 存放，  避免多次请求服务
  static deferrer: IDeferrer | null = null;

  // 礼物数据
  static giftConfig: Record<number, IGiftItem> | null = null;


  static async get() {
    if (import.meta.env.SSR) {
      return this.getFromService();
    }

    let giftConfig = this.getFromCache();

    if (giftConfig) {
      if (!this.isLatest && !this.timer) {
        // 命中缓存后，重新拉取服务端数据更新缓存
        this.timer = window.setTimeout(() => this.getFromService(), 1000 * 30);
      }
    }
    else if (this.deferrer) {
      // 已经有请求服务但未返回的promise 
      giftConfig = await this.deferrer.promise;
    }
    else {
      giftConfig = await this.getFromService();
    }

    if (!this.hasPreloadTail) {
      this.preloadTail(giftConfig);
    }

    return giftConfig;
  }

  static async getFromService() {
    this.deferrer = defer();
    try {
      const { data } = await giftService.getPreloadConfig();
      const giftConfig: Record<number, IGiftItem> = {};

      data.gift.forEach((item: IGiftItem, idx: number) => {
        item.order = idx;
        giftConfig[item.id] = item;
      });

      this.isLatest = true;
      if (!import.meta.env.SSR) {
        this.setCache(giftConfig);
      }

      this.deferrer.resolve(giftConfig);
      return giftConfig;
    } catch (e) {
      this.deferrer.reject(e);
      return null;
    }
  }

  static getFromCache() {
    if (this.giftConfig) {
      return this.giftConfig;
    }
    const giftConfigJSON = Storage.get(STORAGE_KEY);
    if (!giftConfigJSON) {
      return null;
    }
    let giftConfig = null;
    try {
      giftConfig = JSON.parse(giftConfigJSON);
    } catch (e) {
    }
    this.giftConfig = giftConfig;
    return giftConfig;
  }

  static async setCache(giftConfig: Record<number, IGiftItem>) {
    this.giftConfig = giftConfig;
    await Promise.all(Object.values(giftConfig).map(async (item: IGiftItem) => {
      if (item.order < this.base64Count) {
        const base64: string = await imageToBase64(item.base_icon);
        item.base_icon_base64 = base64;
        giftConfig[item.id] = item;
      }
      return item;
    }));
    this.giftConfig = giftConfig;
    return Storage.set(STORAGE_KEY, JSON.stringify(giftConfig));
  }

  static hasCache(): boolean {
    const giftConfig = this.getFromCache();
    return !!giftConfig;
  }

  static clearCache() {
    this.giftConfig = null;
    return Storage.remove(STORAGE_KEY);
  }

  static async preloadTail(giftConfig: Record<number, IGiftItem>) {
    this.hasPreloadTail = true;
    const giftList: IGiftItem[] = Object.values(giftConfig).sort((a, b) => a.order - b.order);
    for (const item of giftList) {
      if (item.order >= this.base64Count) {
        await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.src = item.base_icon;
        });
        await sleep(200);
      }
    }
  }
}


export const useGiftCache = async () => {
  const store = useStore();
  const giftConfig = ref({});

  if (store.state.room.giftConfig) {
    giftConfig.value = store.state.room.giftConfig;
  } else {
    giftConfig.value = await GiftCache.get();
    store.commit('setGiftConfig', giftConfig);
  }

  return giftConfig;
};